import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Plus, Trash2, Loader2, MapPin, Navigation, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const SecurityPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [form, setForm] = useState({ nama: '', latitude: '', longitude: '' });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/security-posts');
      setPosts(res.data);
    } catch (error) {
      console.error('Failed to fetch security posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setForm({ nama: '', latitude: '', longitude: '' });
    setShowDialog(true);
  };

  const useCurrentLocation = () => {
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((f) => ({
          ...f,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        }));
        setGettingLocation(false);
      },
      () => {
        toast.error('Gagal mendapatkan lokasi');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!form.nama.trim()) {
      toast.error('Nama pos wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload = { nama: form.nama.trim() };
      if (form.latitude && form.longitude) {
        payload.latitude = parseFloat(form.latitude);
        payload.longitude = parseFloat(form.longitude);
      }
      await api.post('/security-posts', payload);
      toast.success('Pos keamanan berhasil dibuat');
      setShowDialog(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat pos');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pos keamanan ini?')) return;
    try {
      await api.delete(`/security-posts/${id}`);
      toast.success('Pos berhasil dihapus');
      fetchPosts();
    } catch (error) {
      toast.error('Gagal menghapus pos');
    }
  };

  return (
    <div className="space-y-6" data-testid="security-posts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pos Keamanan</h1>
          <p className="text-muted-foreground text-sm mt-1">Kelola titik patroli & cetak QR code untuk tiap pos</p>
        </div>
        <Button onClick={openDialog} data-testid="add-post-btn">
          <Plus className="w-4 h-4 mr-2" />Tambah Pos
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : posts.length === 0 ? (
        <Card><CardContent className="text-center py-10 text-muted-foreground">Belum ada pos keamanan</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="post-list">
          {posts.map((post) => (
            <Card key={post.id} data-testid={`post-item-${post.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />{post.nama}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDelete(post.id)} data-testid={`delete-post-${post.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-2">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${post.kode_pos}`}
                  alt={`QR ${post.nama}`}
                  className="w-36 h-36 border rounded-lg"
                  data-testid={`post-qr-${post.id}`}
                />
                <p className="text-xs font-mono font-semibold flex items-center gap-1">
                  <QrCode className="w-3 h-3" />{post.kode_pos}
                </p>
                <p className="text-xs text-muted-foreground">{post.latitude != null && post.longitude != null ? `${post.latitude.toFixed(5)}, ${post.longitude.toFixed(5)}` : '-'}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Tambah Pos Keamanan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Pos</Label>
              <Input
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                placeholder="Contoh: Pos Lobby Depan"
                data-testid="post-nama-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Lokasi (opsional)</Label>
              <Button size="sm" variant="outline" className="w-full" onClick={useCurrentLocation} disabled={gettingLocation} data-testid="use-location-btn">
                {gettingLocation ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Mendapatkan lokasi...</> : <><Navigation className="w-4 h-4 mr-1" />Gunakan Lokasi Saat Ini</>}
              </Button>
              {form.latitude && form.longitude && (
                <p className="text-xs text-center text-green-600">{form.latitude}, {form.longitude}</p>
              )}
            </div>
            <Button className="w-full" onClick={handleSave} disabled={saving} data-testid="save-post-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Simpan Pos
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityPostsPage;
